import { computed, signal } from "@preact/signals-core";
import { ApiRequestError } from "../lib/api";
import { bindErrorText, bindText, html, list } from "../lib/bind";
import { formatDate } from "../lib/date";
import { formatMoney } from "../lib/money";
import { labelRow } from "../lib/table";
import { type AgingBucket, type AgingReport, getAgingReport } from "../services/reports";
import { buildReportsSubNav } from "./reports-subnav";

const BUCKET_LABELS: Record<AgingBucket, string> = {
  not_yet_due: "Not yet due",
  days_1_30: "1–30 days overdue",
  days_31_60: "31–60 days overdue",
  days_61_90: "61–90 days overdue",
  days_90_plus: "90+ days overdue",
};

export function reportsAgingView(): Node {
  const loadError = signal("");
  const report = signal<AgingReport | null>(null);

  function reload(): void {
    loadError.value = "";
    getAgingReport()
      .then((result) => {
        report.value = result;
      })
      .catch((err: unknown) => {
        loadError.value = err instanceof ApiRequestError ? err.body.message : "Failed to load report";
      });
  }
  reload();

  const fragment = html`<div class="view">
    <div class="card">
      <div class="page-header"><span class="card-label">Aging</span></div>
      <p class="form-error"></p>
      <p class="field-hint as-of-note"></p>

      <div class="data-table">
        <table>
          <thead><tr><th>Bucket</th><th>Total</th><th>Invoices</th></tr></thead>
          <tbody class="bucket-body"></tbody>
        </table>
      </div>

      <div class="card-label-row"><span class="card-label">Unpaid invoices</span></div>
      <div class="data-table aging-lines-table">
        <table>
          <thead>
            <tr><th>Client</th><th>Invoice</th><th>Due date</th><th>Status</th><th>Amount</th></tr>
          </thead>
          <tbody class="lines-body"></tbody>
        </table>
      </div>
    </div>
  </div>`;

  const viewEl = fragment.querySelector<HTMLElement>(".view");
  if (!viewEl) throw new Error("aging report view produced no .view element");
  viewEl.insertBefore(buildReportsSubNav("aging"), viewEl.firstChild);

  const errorEl = fragment.querySelector<HTMLParagraphElement>(".form-error");
  const asOfEl = fragment.querySelector<HTMLElement>(".as-of-note");
  const bucketTable = fragment.querySelectorAll<HTMLTableElement>(".data-table table")[0];
  const bucketBody = fragment.querySelector<HTMLTableSectionElement>(".bucket-body");
  const linesTable = fragment.querySelector<HTMLTableElement>(".aging-lines-table table");
  const linesBody = fragment.querySelector<HTMLTableSectionElement>(".lines-body");

  if (!errorEl || !asOfEl || !bucketTable || !bucketBody || !linesTable || !linesBody) {
    throw new Error("aging report view markup missing expected elements");
  }
  bindErrorText(errorEl, loadError);
  bindText(
    asOfEl,
    computed(() => (report.value ? `As of ${formatDate(report.value.as_of)}` : "")),
  );

  list(
    bucketBody,
    computed(() => report.value?.bucket_totals ?? []),
    (b) => b.bucket,
    (b) => {
      const row = html`<tr>
        <td>${BUCKET_LABELS[b.bucket]}</td>
        <td>${formatMoney(b.total_gbp)}</td>
        <td>${String(b.count)}</td>
      </tr>`.firstElementChild;
      if (!row) throw new Error("aging bucket row template produced no element");
      return labelRow(row, bucketTable);
    },
  );

  list(
    linesBody,
    computed(() => report.value?.lines ?? []),
    (l) => l.invoice_id,
    (l) => {
      const row = html`<tr>
        <td>${l.client_name}</td>
        <td>${l.invoice_number ?? "—"}</td>
        <td>${formatDate(l.due_date)}</td>
        <td>${BUCKET_LABELS[l.bucket]}</td>
        <td>${formatMoney(l.remainder_gbp)}</td>
      </tr>`.firstElementChild;
      if (!row) throw new Error("aging line row template produced no element");
      return labelRow(row, linesTable);
    },
  );

  return fragment;
}
